'use client';

import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentMethodsRow } from '@/components/checkout/PaymentMethodsRow';
import { EmailTypoHint } from '@/components/ui/email-typo-hint';
import { useParticipant } from '@/lib/participant-auth/context';
import { formatPriceCents } from '@/lib/utils/currency';
import { photoRequestHeaders } from '@/lib/race-photos/client-id';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface PhotoPurchaseFormProps {
  albumId: string;
  /**
   * Where the purchase endpoints live. The album page passes nothing and gets
   * `/api/race-photos/<albumId>`; the event's master link passes its own base,
   * because there IS no album in that scope and albumId arrives empty. Without
   * this the master link posted to `/api/race-photos//purchase`, which is not a
   * route, so buying photos through an event link could never succeed.
   */
  apiBase?: string;
  matchedPhotoIds: string[];
  selfieS3Key: string;
  participantPrice: number;
  currency: string;
  themeColors: ThemeColors;
  onSuccess: (downloadToken: string) => void;
}

function isDarkColor(hexColor: string): boolean {
  const hex = hexColor.replace('#', '');
  const rgb = parseInt(hex, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness < 128;
}

function formatPrice(cents: number, currency: string) {
  return formatPriceCents(cents, currency);
}

function PurchasePaymentForm({ base, matchedPhotoIds, selfieS3Key, email, amount, currency, onSuccess, onError }: {
  base: string;
  matchedPhotoIds: string[];
  selfieS3Key: string;
  email: string;
  amount: number;
  currency: string;
  onSuccess: (downloadToken: string) => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    if (!email.trim()) {
      onError('Please enter your email address');
      return;
    }
    setLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message || 'Validation failed');
        setLoading(false);
        return;
      }
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      });

      if (error) {
        onError(error.message || 'Payment failed');
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const res = await fetch(`${base}/purchase/confirm`, {
          method: 'POST',
          headers: photoRequestHeaders(),
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            selfieS3Key,
            matchedPhotoIds,
            email,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          onSuccess(data.downloadToken);
        } else {
          onError('Payment confirmed but failed to generate download access');
        }
      }
    } catch {
      onError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <PaymentMethodsRow
        termsAccepted={!!email.trim()}
        onWalletConfirm={handlePay}
      />
      <button
        onClick={handlePay}
        disabled={!stripe || !elements || loading}
        style={{
          width: '100%', padding: '14px 24px', borderRadius: '8px',
          background: 'var(--fv-accent)', color: 'var(--fv-accent-text, #fff)',
          border: 'none', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
          opacity: (!stripe || !elements || loading) ? 0.5 : 1,
        }}
      >
        {loading ? 'Processing...' : `Pay ${formatPrice(amount, currency)} & Download`}
      </button>
    </div>
  );
}

/**
 * Inline purchase form rendered within the photo finder page (not a modal).
 * Collects email and payment. Uses Stripe theme matching the event theme.
 */
export function PhotoPurchaseForm({ albumId, apiBase, matchedPhotoIds, selfieS3Key, participantPrice, currency, themeColors, onSuccess }: PhotoPurchaseFormProps) {
  const base = apiBase ?? `/api/race-photos/${albumId}`;
  const participant = useParticipant();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [email, setEmail] = useState(participant?.email || '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initiated, setInitiated] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  const handleInitiatePurchase = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${base}/purchase`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ selfieS3Key, matchedPhotoIds, email }),
      });

      if (!res.ok) {
        // Surface what the server actually said. This used to throw a fixed
        // "Failed to initiate purchase", which discarded the real reason and
        // left both the buyer and anyone debugging with nothing to go on:
        // "Organiser payment not configured", "These photos are not available
        // yet" and a 429 all looked identical.
        const detail = await res.json().catch(() => null);
        throw new Error(
          detail?.message
            || (res.status === 429
              ? 'Too many attempts. Please wait a moment and try again.'
              : 'Could not start the purchase. Please try again.'),
        );
      }

      const data = await res.json();

      if (data.free) {
        onSuccess(data.downloadToken);
        return;
      }

      setClientSecret(data.clientSecret);
      setInitiated(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Build Stripe appearance to match event theme
  const darkBg = isDarkColor(themeColors.primary);
  const stripeAppearance = darkBg
    ? {
        theme: 'night' as const,
        variables: {
          colorPrimary: themeColors.secondary,
          colorBackground: '#1a1a1a',
          colorText: '#e5e5e5',
        },
      }
    : {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: themeColors.secondary,
        },
      };

  return (
    <div style={{ maxWidth: '500px', margin: '0 auto', width: '100%' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--fv-text-primary)', margin: '0 0 8px' }}>
          Unlock Your Photos
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--fv-text-secondary)', margin: 0 }}>
          <strong>{matchedPhotoIds.length}</strong> photo{matchedPhotoIds.length !== 1 ? 's' : ''} found - download in full resolution
        </p>
      </div>

      <div style={{
        padding: '16px', borderRadius: '10px', marginBottom: '24px',
        textAlign: 'center', border: '1px solid var(--fv-border)',
        background: 'var(--fv-bg-secondary, rgba(0,0,0,0.05))',
      }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--fv-text-primary)' }}>
          {formatPrice(participantPrice, currency)}
        </div>
      </div>

      {/* Email input - shown before Stripe form is loaded */}
      {!initiated && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--fv-text-secondary)', marginBottom: '6px' }}>
            Email for receipt
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
            placeholder="your@email.com"
            style={{
              width: '100%', padding: '12px 14px', borderRadius: '8px',
              border: emailFocused ? '2px solid var(--fv-accent)' : '1px solid var(--fv-border)',
              fontSize: '15px',
              boxSizing: 'border-box', background: 'var(--fv-bg-secondary, #f8f8f8)',
              color: 'var(--fv-text-primary)',
              outline: 'none',
              transition: 'border-color 0.15s',
            }}
          />
          {/* The receipt and the download link both go here. Nothing bounces
              back to the person who typed it. */}
          <EmailTypoHint email={email} onAccept={setEmail} />
        </div>
      )}

      {!initiated && (
        <button
          onClick={handleInitiatePurchase}
          disabled={loading || !email.trim()}
          style={{
            width: '100%', padding: '14px 24px', borderRadius: '8px',
            background: 'var(--fv-accent)', color: 'var(--fv-accent-text, #fff)',
            border: 'none', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
            opacity: (loading || !email.trim()) ? 0.5 : 1,
            marginBottom: '16px',
          }}
        >
          {loading ? 'Loading...' : 'Continue to Payment'}
        </button>
      )}

      {error && (
        <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px', color: '#EF4444', fontSize: '13px', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
          <PurchasePaymentForm
            base={base}
            matchedPhotoIds={matchedPhotoIds}
            selfieS3Key={selfieS3Key}
            email={email}
            amount={participantPrice}
            currency={currency}
            onSuccess={onSuccess}
            onError={setError}
          />
        </Elements>
      )}
    </div>
  );
}
